import type { CompanyListing } from "@pentacode/core";
import { LitElement, css, html, nothing } from "lit";
import { customElement, state } from "lit/decorators.js";
import { alert } from "../../elements/alert-dialog";
import "../../elements/scroller";
import "../../elements/terms/accept-terms-dialog";
import type { AcceptTermsDialog } from "../../elements/terms/accept-terms-dialog";
import { app, appMode } from "../../init";
import { restart } from "../../lib/restart";
import { singleton } from "../../lib/singleton";
import { showAcceptTermsDialog } from "../../lib/util";
import { Routing } from "../../mixins/routing";
import { StateMixin } from "../../mixins/state";
import { mixins, shared } from "../../styles";

@customElement("ptc-select-company")
export class SelectCompany extends Routing(StateMixin(LitElement)) {
    routePattern = /^select-company/;

    @state()
    private _loading = false;

    @state()
    private _companies: CompanyListing[] = [];

    @state()
    private _filter = "";

    @singleton("ptc-accept-terms-dialog")
    private _acceptTermsDialog: AcceptTermsDialog;

    handleRoute() {
        void this._load();
    }

    private async _load() {
        this._loading = true;

        try {
            this._companies = await app.api.listCompanies();

            if (this._companies.length === 1) {
                await this._selectCompany(this._companies[0].id);
                return;
            }
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }

        this._loading = false;
    }

    private async _selectCompany(companyId: number) {
        this._loading = true;

        try {
            await app.selectCompany(companyId);
            const { next } = this.router.params;

            const termsState = await app.termsNeedToBeAccepted();
            if (termsState) {
                await showAcceptTermsDialog(this._acceptTermsDialog, app, termsState);
            }

            if (appMode === "embedded") {
                // A full-page navigation would tear down the partner host
                // page; ask the wrapper for a dispose/re-mount cycle at the
                // `next` target instead (PTC-3919) — the embedded counterpart
                // of the location.assign() below.
                restart(next || "");
                return;
            }

            window.location.assign(this.router.basePath + (next || ""));
        } catch (e) {
            void alert(e.message, { type: "warning" });
        }

        this._loading = false;
    }

    static styles = [
        shared,
        css`
            :host {
                ${mixins.fullbleed()};
                ${mixins.scroll()};
                z-index: 100;
                background: var(--color-bg);
                padding: 2em;
            }

            .container {
                min-height: 100%;
                text-align: center;
            }

            .card {
                width: 300px;
                background: var(--color-bg);
                border-radius: calc(2 * var(--border-radius));
                color: var(--color-fg);
                margin: 2em;
                box-shadow: rgba(0, 0, 0, 0.5) 0 0 10px -5px;
                position: relative;
                overflow: hidden;
                max-height: 30em;
                padding-top: 1em;
            }

            .card > input,
            .companies > * {
                margin: 0 1em 1em 1em;
            }

            .logo {
                height: 50px;
            }

            .greeting {
                margin-top: 1em;
                width: 350px;
                text-align: center;
            }

            .message {
                margin: 0 0 0.8em 0;
            }

            .fallback {
                gap: 1rem;
            }
        `,
    ];

    render() {
        if (!app.account) {
            return;
        }

        const fs = this._filter.toLowerCase();

        return html`
            <div class="vertical center-aligning center-justifying layout container">
                <ptc-ui-logo variant="full" class="logo"></ptc-ui-logo>

                ${this._companies.length === 0 && !this._loading
                    ? html`<div class="card vertical layout double-padded fallback">
                          <p>
                              Leider konnten wir keine zugeordneten Unternehmen finden.
                              <span class="emoji">😕</span>
                          </p>
                          <p>Bitte wenden Sie sich an einen Manager Ihres Unternehmens, um Zugriff zu erhalten.</p>

                          <button
                              @click=${async () => {
                                  await app.logout();
                                  restart();
                              }}
                          >
                              Ausloggen
                          </button>
                      </div>`
                    : nothing}
                ${this._companies.length > 0
                    ? html`<div class="greeting">Bitte wählen Sie ein Unternehmen:</div>
                          <div class="card vertical layout">
                              <input
                                  type="search"
                                  placeholder="Filtern..."
                                  @input=${(e: Event) => (this._filter = (e.target as HTMLInputElement).value)}
                                  ?hidden=${this._companies.length < 5}
                              />

                              <ptc-scroller class="stretch">
                                  <div class="vertical layout companies">
                                      ${this._companies
                                          .filter(
                                              (c) =>
                                                  !fs ||
                                                  c.name.toLowerCase().includes(fs) ||
                                                  c.venues.some((v) => v.name.toLowerCase().includes(fs)) ||
                                                  c.id.toString() === fs
                                          )
                                          .map(
                                              (company) => html`
                                                  <button class="ghost" @click=${() => this._selectCompany(company.id)}>
                                                      ${company.name}
                                                  </button>
                                              `
                                          )}
                                  </div>
                              </ptc-scroller>
                          </div>

                          <button class="transparent" ?hidden=${!app.company} @click=${() => this.go("")}>
                              Abbrechen
                          </button>`
                    : nothing}

                <div
                    class="fullbleed center-aligning center-justifying vertical layout scrim"
                    ?hidden=${!this._loading}
                >
                    <ptc-ui-spinner ?active=${this._loading}></ptc-ui-spinner>
                </div>
            </div>
        `;
    }
}
